import { useState } from "react";
import { DragEndEvent, DragStartEvent } from "@dnd-kit/core";
import { CourseLite } from "../types/course.response";
import { Requirement } from "../types/requirement.response";
import { CourseRequirementOverride } from "../services/api/user-courses.api";

export interface DragData {
    course: CourseLite;
    isApplied: boolean;
    nodeId: string;
}

export function useCourseSetDnd(
    requirement: Requirement,
    onOverride: (course: CourseLite, override: CourseRequirementOverride) => void | Promise<void>
) {
    const [activeDrag, setActiveDrag] = useState<DragData | null>(null);

    const handleDragStart = (event: DragStartEvent) => {
        const data = event.active.data.current as DragData | undefined;
        if (data?.course) {
            setActiveDrag(data);
        }
    };

    const handleDragEnd = async (event: DragEndEvent) => {
        setActiveDrag(null);
        const { active, over } = event;
        const dragData = active.data.current as DragData | undefined;
        const dropData = over?.data.current as { type: string; course?: CourseLite; nodeId?: string } | undefined;
        if (!over) return;
        if (!dragData?.course || !dropData?.type) return;

        const requirementId = requirement.info.id;

        if (dropData.type === "remove-zone" && dragData.isApplied) {
            if (!dragData.course.userItemId) return;
            await onOverride(dragData.course, {
                requirementId,
                nodeId: dragData.nodeId,
                overrideType: "FORCE_UNAPPLY",
            });
        } else if (dropData.type === "empty-slot" && !dragData.isApplied) {
            const targetNodeId = dropData.nodeId ?? dragData.nodeId;
            if (!dragData.course.userItemId || !targetNodeId) return;
            await onOverride(dragData.course, {
                requirementId,
                nodeId: targetNodeId,
                overrideType: "FORCE_APPLY",
            });
        } else if (dropData.type === "chip" && !dragData.isApplied && dropData.course) {
            const targetNodeId = dropData.nodeId ?? dragData.nodeId;
            if (!dragData.course.userItemId || !dropData.course.userItemId) return;
            await onOverride(dragData.course, {
                requirementId,
                nodeId: targetNodeId,
                overrideType: "FORCE_APPLY",
            });
            await onOverride(dropData.course, {
                requirementId,
                nodeId: targetNodeId,
                overrideType: "FORCE_UNAPPLY",
            });
        }
    };

    const handleDragCancel = () => {
        setActiveDrag(null);
    };

    return { activeDrag, handleDragStart, handleDragEnd, handleDragCancel };
}
